<script setup lang="ts">
const props = defineProps<{
  breadcrumbs?: {
    slug: string
    title: string
  }[]
  blogHomepageItems: any
  showBlogTitle?: boolean
}>()

const appConfig = useAppConfig()
const blog = appConfig.blog
const blogNavigation = props.blogHomepageItems.navigation

const state = reactive({
  isMenuOpen: false,
  mobileClickedItems: [] as number[],
  navigationArticle: null,
  selectedMenuId: 0,
})

function setNavigationArticle(val?: { article: StoryBlokArticle }) {
  if (val?.article)
    state.navigationArticle = val.article
  else
    state.navigationArticle = props.blogHomepageItems.navigation.article
}

function getImageSource(img: StoryBlokImage) {
  return img.filename.replace('https://a.storyblok.com', 'https://img2.storyblok.com/360x220')
}

function toggleMobileItem(id: number) {
  if (state.mobileClickedItems.includes(id))
    state.mobileClickedItems.splice(state.mobileClickedItems.indexOf(id), 1)

  else
    state.mobileClickedItems.push(id)
}
</script>

<template>
  <div class="bg-white">
    <div class="mx-auto max-w-[1160px] px-5 xl:px-0 py-2.5">
      <AppBreadcrumbs :items="breadcrumbs" class="text-gray-dark" />
      <StoryblokLink v-if="showBlogTitle" :to="blog.url" class="flex my-5 headline-1">
        {{ blogHomepageItems.title }}
      </StoryblokLink>
    </div>

    <div class="bg-primary">
      <div class="mx-auto max-w-[1160px] px-5 xl:px-0">
        <div class="m-0 p-0 items-center h-57px -ml-25px hidden lg:flex">
          <div
            v-for="(item, i) in blogNavigation"
            :key="i"
            class="nav-item"
            :class="{ 'has-sub': item.submenu }"
          >
            <StoryblokLink
              :to="item.url"
              active-class="active"
              class="nav-item"
              :class="{ 'has-sub': item.submenu }"
              @mouseenter="setNavigationArticle(item)"
            >
              <div class="item" @mouseleave="setNavigationArticle(null)">
                <div class="item-text">
                  {{ item.name }}
                  <span class="icon-rotate">
                    <AppIcon size="17" icon="far-angle-down" />
                  </span>
                </div>

                <object class="submenu hidden shadow-hover flex-row absolute z-10 top-52px left-0 flex flex-cols p-5 rounded-b-xl rounded-r-xl text-base font-medium text-secondary bg-white">
                  <div class="flex flex-col w-[260px] mr-5 font-medium flex-shrink-0">
                    <StoryblokLink
                      v-for="(sub, j) in item.submenu" :key="j"
                      :to="sub.url"
                      class="submenu-item pt-5"
                      @mouseenter="setNavigationArticle(sub)"
                    >
                      <AppIcon size="16" :icon="sub.icon" />{{ sub.name }}
                    </StoryblokLink>
                    <hr class="my-2.5 border-gray-light">
                    <StoryblokLink v-if="item.glossary" :to="item.glossary" class="submenu-item">
                      <AppIcon size="16" icon="far-clipboard-list" /> {{ item.glossaryName }}
                    </StoryblokLink>
                    <StoryblokLink v-if="item.documents" :to="item.documents" class="submenu-item pt-5">
                      <AppIcon size="16" icon="far-file-check" /> {{ item.documentsName }}
                    </StoryblokLink>
                  </div>
                  <StoryblokLink
                    v-if="state.navigationArticle"
                    :key="state.navigationArticle.slug"
                    :to="`${blog.url}/${state.navigationArticle.slug}`"
                    class="w-360px h-220px hover:shadow-hover relative"
                  >
                    <img
                      :src="getImageSource(state.navigationArticle.content.image)"
                      class="rounded-xl z-10 w-auto h-220px"
                      :alt="state.navigationArticle.content.image.alt"
                    >
                    <div
                      class="absolute top-0 w-full h-220px rounded-xl bg-gradient-to-b from-transparent to-black opacity-60 z-11 overflow-hidden"
                    />
                    <div class="z-20 absolute bottom-5 left-5 text-white">
                      <h4 class="text-shadow pb-0.5 leading-tight">
                        {{ state.navigationArticle.content.title }}
                      </h4>
                      <div class="flex flex-wrap text-sm leading-16px">
                        <div class="pr-2.5 leading-16px">
                          {{ useFormatDate(state.navigationArticle.content.publishDate, 'd. M. yyyy') }}
                        </div>
                        <div>|</div>
                        <div class="px-2.5">
                          {{ blog.articleLength }}: {{ state.navigationArticle.content.readTime }} min.
                        </div>
                      </div>
                    </div>
                  </StoryblokLink>
                </object>
              </div>
            </StoryblokLink>
          </div>
          <div v-if="blogHomepageItems.navigationLink.length" class="flex h-full">
            <StoryblokLink
              v-for="(linkItem, i) in blogHomepageItems.navigationLink"
              :key="i"
              v-bind="linkItem.link"
              class="nav-item"
            >
              <div class="item">
                <span class="item-text">{{ linkItem.text }}</span>
              </div>
            </StoryblokLink>
          </div>
        </div>
        <div class="lg:hidden">
          <div
            class="flex items-center h-47px text-white text-lg font-semibold justify-between"
            @click="state.isMenuOpen = !state.isMenuOpen"
          >
            {{ blog.category || 'Kategorie' }}
            <span :class="{ 'rotate-icon': state.isMenuOpen }">
              <AppIcon size="18" icon="far-angle-down" />
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Navigation -->
    <div v-if="state.isMenuOpen" class="lg:hidden bg-primary absolute w-full z-20 pb-2.5">
      <hr class="mx-2.5 border-gray opacity-15">
      <div
        v-for="(item, i) in blogNavigation"
        class="nav-item-mobile has-sub"
        @click="toggleMobileItem(i)"
      >
        <div class="item relative w-full">
          <div class="item-text">
            {{ item.name }}
            <span class="ml-2" :class="{ 'rotate-icon': state.mobileClickedItems.includes(i) }">
              <AppIcon size="18" class="icon-sub" icon="far-angle-down" />
            </span>
          </div>

          <div v-if="state.mobileClickedItems.includes(i)" class="px-5 text-base font-medium">
            <div class="block mr-5 font-medium">
              <StoryblokLink :to="item.url" class="submenu-item">
                <AppIcon size="16" icon="far-newspaper" class="pr-2" />{{ blog.allArticles }}
              </StoryblokLink>
              <StoryblokLink v-for="(sub, j) in item.submenu" :key="j" :to="sub.url" class="submenu-item">
                <AppIcon size="16" :icon="sub.icon" class="pr-2" />{{ sub.name }}
              </StoryblokLink>
              <hr class="my-2.5 mx-2.5 border-gray opacity-15">
              <StoryblokLink v-if="item.glossary" :to="item.glossary" class="submenu-item">
                <AppIcon size="16" icon="far-clipboard-list" class="pr-2" /> {{ item.glossaryName }}
              </StoryblokLink>
              <StoryblokLink :to="item.documents" class="submenu-item pt-5">
                <AppIcon size="16" icon="far-file-check" class="pr-2" /> {{ item.documentsName }}
              </StoryblokLink>
            </div>
          </div>
        </div>
      </div>
      <StoryblokLink
        v-if="blogHomepageItems.navigationLink.length"
        v-bind="blogHomepageItems.navigationLink[0].link"
        class="nav-item-mobile"
      >
        <span class="item-text">{{ blogHomepageItems.navigationLink[0].text }}</span>
      </StoryblokLink>
      <div v-if="blogHomepageItems.navigationLink.length" class="h-full">
        <StoryblokLink
          v-for="(linkItem, i) in blogHomepageItems.navigationLink"
          :key="i"
          class="nav-item-mobile"
          v-bind="linkItem.link"
        >
          <span class="item-text">{{ linkItem.text }}</span>
        </StoryblokLink>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.nav-item {
  @apply text-white hover:text-secondary relative text-lg mr-5px font-semibold h-full flex;
  .item {
    @apply relative top-5px h-47px w-full;
  }
  .item-text {
    @apply rounded-t-xl h-full relative z-11 px-25px rounded-t-xl flex items-center;
    .icon-rotate {
      @apply ml-2 p-0 origin-center transition;
    }
  }

  &:hover {
    .submenu {
      @apply flex;
    }
    .item-text {
      @apply bg-white;
    }
    .icon-rotate {
      @apply transform rotate-180;
    }
  }
  &.active:not(:hover) .item-text {
    @apply bg-white bg-opacity-40 text-secondary;
  }
  &:not(.has-sub) .item-text {
    @apply rounded-b-xl;
  }
  &.has-sub .item {
    @apply h-52px rounded-b-0;
  }

  .submenu-item {
    @apply px-2.5;
  }
}

.rotate-icon {
  @apply transform rotate-180 transition;
}

.nav-item-mobile {
  @apply text-white relative text-lg font-semibold h-full flex;
  .item-text {
    @apply  px-5 flex items-center h-47px
  }
  .icon {
    @apply pl-2.5;
  }

  .icon-sub {
    @apply p-0;
  }

  .submenu-item {
    @apply mx-10px;
  }
}

.submenu-item {
  @apply flex gap-2.5 items-center py-2.5 rounded-lg transition hover:bg-primary hover:bg-opacity-15;
}
</style>
